import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import policyPageService from "./policyService";

const errMsg = (error) =>
  error.response?.data?.message || error.message || "Something went wrong";

const makeThunk = (type, fn) =>
  createAsyncThunk(type, async (arg, thunkAPI) => {
    try {
      return await fn(arg);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || { message: errMsg(error) });
    }
  });

export const fetchPolicies = makeThunk("policyPage/fetchAll", async () => (await policyPageService.getAll()).data);
export const createPolicy = makeThunk("policyPage/create", async (data) => (await policyPageService.create(data)).data);
export const updatePolicy = makeThunk("policyPage/update", async (data) => (await policyPageService.update(data)).data);
export const deletePolicy = makeThunk("policyPage/delete", async (id) => {
  await policyPageService.remove(id);
  return id;
});

const bySort = (a, b) => a.sort_order - b.sort_order || a.id - b.id;

const policySlice = createSlice({
  name: "policy",
  initialState: {
    list: [],
    isLoading: false,
    isSaving: false,
    isError: false,
    message: "",
    errors: {},
  },
  reducers: {
    clearPolicyError: (s) => { s.isError = false; s.message = ""; s.errors = {}; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPolicies.pending, (s) => { s.isLoading = true; })
      .addCase(fetchPolicies.fulfilled, (s, a) => { s.isLoading = false; s.list = a.payload; })
      .addCase(fetchPolicies.rejected, (s, a) => {
        s.isLoading = false; s.isError = true;
        s.message = a.payload?.message || "Failed to load policies";
      })

      .addCase(createPolicy.pending, (s) => { s.isSaving = true; s.isError = false; s.errors = {}; })
      .addCase(createPolicy.fulfilled, (s, a) => { s.isSaving = false; s.list.push(a.payload); s.list.sort(bySort); })
      .addCase(createPolicy.rejected, (s, a) => {
        s.isSaving = false; s.isError = true;
        s.message = a.payload?.message || "Create failed";
        s.errors = a.payload?.errors || {};
      })

      .addCase(updatePolicy.pending, (s) => { s.isSaving = true; s.isError = false; s.errors = {}; })
      .addCase(updatePolicy.fulfilled, (s, a) => {
        s.isSaving = false;
        s.list = s.list.map((p) => (p.id === a.payload.id ? a.payload : p)).sort(bySort);
      })
      .addCase(updatePolicy.rejected, (s, a) => {
        s.isSaving = false; s.isError = true;
        s.message = a.payload?.message || "Update failed";
        s.errors = a.payload?.errors || {};
      })

      .addCase(deletePolicy.pending, (s) => { s.isSaving = true; s.isError = false; })
      .addCase(deletePolicy.fulfilled, (s, a) => { s.isSaving = false; s.list = s.list.filter((p) => p.id !== a.payload); })
      .addCase(deletePolicy.rejected, (s, a) => {
        s.isSaving = false; s.isError = true; s.message = a.payload?.message || "Delete failed";
      });
  },
});

export const { clearPolicyError } = policySlice.actions;
export default policySlice.reducer;